import {Typography} from '@mui/material';
import React, {Fragment, useEffect} from 'react';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AbandonFlowModal,
	FailToCompleteActionModal,
	StepProgressBar,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {MainHeader} from 'presentation/components/MainHeader';
import {PageWrapperLayout} from 'presentation/components/PageWrapperLayout';
import {FeaturesEnum, OperationStateTypesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new/';
import {BeneficiaryData} from '../../components';
import {ConclusionModal} from '../../components/ConclusionModal';
import {RefundVariantEnum} from '../../constants/refundVariantEnum';
import {useBackButtonExitModal} from '../../hooks';
import {useRequestPreview} from '../../hooks/useRequestPreview';
import {useResetRefundFlow} from '../../hooks/useResetRefundFlow';
import {Conference, Finished, ServiceTypeForm, UploadDocuments} from './components';
import {Procedures} from './components/Procedures';
import {SelectTypeV2} from './components/SelectTypeV2/index';
import {useQueryParamsSync} from './hooks/useQueryParamsSync';
import {useRequestPreviewDataStore} from './store/useRequestPreviewDataStore';
import {useRequestPreviewFlowStore} from './store/useRequestPreviewFlowStore';
import {StyledStack, StyledStepsContent} from './styles';
import {StepPreviewEnum, StepPreviewEnumV2} from './types';

const {title: refundTitle, discardModal} = translations['pt-br'].privateFlow.previewRefund;

const {
	description: errorDescription,
	headerTitle: errorHeaderTitle,
	title: errorTitle,
} = translations['pt-br'].privateFlow.refund.previewServiceType.errorModal;

const {tryAgain, goToHome} = translations['pt-br'].commons;

export const RequestPreview = () => {
	const {isTablet, isMobile} = useMediaQueries();
	const requestPreviewData = useRequestPreviewDataStore(state => state.data);
	const {resetRefundFlow} = useResetRefundFlow();
	const resetFlow = useRequestPreviewFlowStore(state => state.resetFlow);
	const setUseServiceTypeFormV2 = useRequestPreviewFlowStore(
		state => state.setUseServiceTypeFormV2,
	);
	const {isEnabled: isRefundPreviewCalcEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_PREVIEW_CALC,
	);

	const {
		IS_ERROR,
		IS_LOADING,
		conclusionModalState,
		exitFlowModalState,
		handleAbandonFlowModalAction,
		handleBack,
		handleBackToRefundMenu,
		handleClose,
		handleGoToHome,
		handleGoToNextStep,
		stepsLabels,
		stepIndexForStepper,
		title,
		step,
		setExitFlowModalState,
		setConclusionModalState,
		setConcludeRequestPreviewState,
		useServiceTypeFormV2,
	} = useRequestPreview();

	useQueryParamsSync({
		step,
		protocol: requestPreviewData.request,
	});

	const pagesByStep = {
		[StepPreviewEnum.BENEFICIARY_DATA]: (
			<BeneficiaryData
				variant={RefundVariantEnum.PREVIEW}
				handleNextStep={handleGoToNextStep}
			/>
		),
		[StepPreviewEnum.SERVICE_TYPE]: <ServiceTypeForm handleNextStep={handleGoToNextStep} />,
		[StepPreviewEnum.UPLOAD_DOCUMENTS]: <UploadDocuments handleNextStep={handleGoToNextStep} />,
		[StepPreviewEnum.REVIEW]: (
			<Conference handleNextStep={handleGoToNextStep} isLoading={IS_LOADING} />
		),
	};

	const pagesByStepV2 = {
		[StepPreviewEnumV2.BENEFICIARY_DATA]: (
			<BeneficiaryData
				variant={RefundVariantEnum.PREVIEW}
				handleNextStep={handleGoToNextStep}
			/>
		),
		[StepPreviewEnumV2.SERVICE_TYPE]: <SelectTypeV2 />,
		[StepPreviewEnumV2.PROCEDURES]: <Procedures handleNextStep={handleGoToNextStep} handleGoToHome={handleGoToHome} />,
		[StepPreviewEnumV2.RESULT]: <Finished onBackToRefundMenu={handleBackToRefundMenu} />,
	};

	useBackButtonExitModal({
		setExitModal: setExitFlowModalState,
		shouldOpenModal:
			step !== StepPreviewEnum.BENEFICIARY_DATA &&
			step !== StepPreviewEnumV2.BENEFICIARY_DATA,
	});

	useEffect(() => {
		resetRefundFlow();
		resetFlow();
		setUseServiceTypeFormV2(isRefundPreviewCalcEnabled);
	}, [resetRefundFlow, resetFlow, setUseServiceTypeFormV2, isRefundPreviewCalcEnabled]);

	return (
		<Fragment>
			<PageWrapperLayout title={refundTitle}>
				<MainHeader
					dataTagId={{
						closeButton: RefundTaggingEnum.CLICK_FECHAR_PREVIA_REEMBOLSO,
						backButton: RefundTaggingEnum.CLICK_VOLTAR_STEP_PREVIA_REEMBOLSO,
					}}
					variant="subRoute"
					title={refundTitle}
					handleBack={handleBack}
					handleClose={handleClose}
					hideBackBtn={
						step === StepPreviewEnum.REVIEW || step === StepPreviewEnumV2.RESULT
					}>
					<StyledStack gap={isTablet ? 2 : 3}>
						{isMobile ? (
							<StepProgressBar
								mt={1}
								pl={1}
								currentStep={Math.min(stepIndexForStepper + 1, stepsLabels.length)}
								totalSteps={stepsLabels.length}
							/>
						) : (
							<CustomStepper
								activeStep={stepIndexForStepper}
								stepLabels={stepsLabels}
							/>
						)}
						{title ? (
							<Typography pl={1} variant="h6" mb={1} mt={1}>
								{title}
							</Typography>
						) : null}
					</StyledStack>
				</MainHeader>
				<StyledStepsContent>
					{useServiceTypeFormV2 ? pagesByStepV2[step] : pagesByStep[step]}
				</StyledStepsContent>
			</PageWrapperLayout>

			<ConclusionModal
				variant={RefundVariantEnum.PREVIEW}
				protocol={requestPreviewData.request}
				open={conclusionModalState}
				setOpen={setConclusionModalState}
			/>

			<AbandonFlowModal
				modaldataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_ABANDONO_PREVIA_REEMBOLSO}
				closeButtondataTagId={
					RefundTaggingEnum.CLICK_FECHAR_ICONE_MODAL_ABANDONO_PREVIA_REEMBOLSO
				}
				confirmButtondataTagId={
					RefundTaggingEnum.CLICK_CONFIRMAR_BOTAO_MODAL_ABANDONO_PREVIA_REEMBOLSO
				}
				backButtondataTagId={RefundTaggingEnum.CLICK_VOLTAR_MODAL_ABANDONO_PREVIA_REEMBOLSO}
				open={exitFlowModalState}
				setOpen={setExitFlowModalState}
				handleConfirm={handleAbandonFlowModalAction}
				title={discardModal.title}
				headerTitle={discardModal.headerTitle}
				description={discardModal.description}
			/>

			<FailToCompleteActionModal
				primaryBtnDataTagId={RefundTaggingEnum.CLICK_VOLTAR_PARA_PAGINA_INICIAL_PREVIA}
				secondaryBtnDataTagId={RefundTaggingEnum.CLICK_TENTAR_NOVAMENTE_PREVIA}
				headerDataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_ERRO_PREVIA_REEMBOLSO}
				headerTitle={errorHeaderTitle}
				description={errorDescription}
				title={errorTitle}
				open={IS_ERROR}
				setOpen={() => setConcludeRequestPreviewState(OperationStateTypesEnum.NONE)}
				primaryActionText={tryAgain}
				secondaryActionText={goToHome}
				fallbackAction={handleGoToHome}
				handleTryAgain={handleGoToNextStep}
			/>
		</Fragment>
	);
};
